import type {
  NachaFile,
  NachaParseResult,
  FileHeader,
  BatchHeader,
  BatchControl,
  FileControl,
  EntryDetail,
  AddendaRecord,
  Batch,
  ValidationError,
  ValidationResult,
  SecCode,
} from '@/types/nacha';

/**
 * Valid Standard Entry Class (SEC) Codes
 * Reference: https://achdevguide.nacha.org/ach-file-details
 */
const VALID_SEC_CODES: SecCode[] = [
  'ARC', // Accounts Receivable Entries
  'BOC', // Back Office Conversion
  'CCD', // Corporate Credit or Debit
  'CIE', // Customer Initiated Entries
  'CTX', // Corporate Trade Exchange
  'IAT', // International ACH Transaction
  'POP', // Point of Purchase
  'POS', // Point of Sale
  'PPD', // Pre-arranged Payment or Deposit
  'RCK', // Re-presented Check Entries
  'TEL', // Telephone Initiated Entries
  'WEB', // Internet-Initiated/Mobile Entries
];

/**
 * Parse NACHA file
 */
/**
 * Validate if content is likely a text file (not binary)
 */
function isTextContent(content: string): boolean {
  // Check for binary/non-printable characters
  // eslint-disable-next-line no-control-regex
  const binaryPattern = /[\x00-\x08\x0E-\x1F\x7F-\xFF]/g;
  const sample = content.substring(0, Math.min(2000, content.length));
  
  // If more than 10% of characters are binary, it's likely not a text file
  const binaryMatches = sample.match(binaryPattern);
  const binaryRatio = binaryMatches ? binaryMatches.length / sample.length : 0;
  
  if (binaryRatio > 0.1) {
    console.log(`❌ Binary content detected: ${(binaryRatio * 100).toFixed(1)}% non-printable characters`);
    return false;
  }
  
  return true;
}

/**
 * Validate basic NACHA file structure before parsing
 * Returns warnings but doesn't throw for recoverable issues
 */
function validateNachaStructure(content: string): string[] {
  console.log('🔍 Validating NACHA structure...');
  const warnings: string[] = [];
  
  // Check if content is empty
  if (!content || content.trim().length === 0) {
    throw new Error('File is empty. Please upload a valid NACHA file.');
  }

  // Check if content is text
  if (!isTextContent(content)) {
    throw new Error('Invalid file format: File appears to be binary (not a text file). Please upload a valid NACHA/ACH text file.');
  }

  // Check minimum length (soft check - allow short files with warning)
  if (content.length < 376) {
    warnings.push(`File is short: ${content.length} characters (typical minimum is 376). File may be incomplete.`);
  }

  // Check maximum reasonable length (prevent parsing huge files)
  if (content.length > 50 * 1024 * 1024) { // 50MB
    throw new Error('File too large: Content exceeds 50MB. This may not be a valid NACHA file.');
  }

  // Check for valid NACHA record type codes at the start
  const firstChar = content.trim()[0];
  if (firstChar !== '1') {
    throw new Error(`Invalid file format: NACHA files must start with '1' (File Header Record), found '${firstChar}'. This does not appear to be a valid NACHA file.`);
  }

  const lines = content.split(/\r?\n/).filter(line => line.length > 0);
  
  if (lines.length < 4) {
    warnings.push(`File has only ${lines.length} lines (typical minimum is 4). File may be incomplete.`);
  }

  console.log(`✅ NACHA structure validation completed (${lines.length} lines, ${warnings.length} warnings)`);
  return warnings;
}

/**
 * Yield control to browser to prevent UI freezing
 */
async function yieldToMain(): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, 0));
}

export async function parseNacha(input: File | string): Promise<NachaParseResult> {
  const startTime = performance.now();
  
  try {
    let content: string;
    
    if (input instanceof File) {
      // Validate file extension
      const fileName = input.name.toLowerCase();
      const validExtensions = ['.txt', '.ach', '.nacha', '.dat'];
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
      
      if (!hasValidExtension) {
        throw new Error(`Invalid file type: "${input.name}". Please upload a NACHA file with extension: ${validExtensions.join(', ')}`);
      }

      // Check file size (NACHA files are typically small, < 10MB)
      if (input.size > 10 * 1024 * 1024) {
        throw new Error('File too large: NACHA files should typically be less than 10MB.');
      }

      content = await input.text();
    } else {
      content = input;
    }

    // Validate NACHA structure before parsing (soft validation)
    validateNachaStructure(content);
    
    // Yield to browser for large files
    if (content.length > 100000) {
      await yieldToMain();
    }
    
    const lines = content.split(/\r?\n/).filter(line => line.length > 0);
    
    // Yield again after splitting large files
    if (lines.length > 1000) {
      await yieldToMain();
    }
    
    const file = await parseFileAsync(lines, content);
    
    // Yield before validation
    if (lines.length > 500) {
      await yieldToMain();
    }
    
    const validation = validateFile(file);
    
    const parseTime = performance.now() - startTime;
    
    return {
      success: true,
      file,
      validation,
      parseTime,
    };
  } catch (error) {
    const parseTime = performance.now() - startTime;
    return {
      success: false,
      validation: {
        valid: false,
        errors: [{
          type: 'error',
          field: 'file',
          message: error instanceof Error ? error.message : 'Unknown parse error',
        }],
        warnings: [],
      },
      parseTime,
    };
  }
}

async function parseFileAsync(lines: string[], raw: string): Promise<NachaFile> {
  if (lines.length < 4) {
    throw new Error('File too short - minimum 4 lines required (file header, batch header, batch control, file control)');
  }
  
  const validationErrors: ValidationError[] = [];
  
  // Parse file header (line 1)
  let fileHeader: FileHeader;
  try {
    fileHeader = parseFileHeader(lines[0], 1, validationErrors);
  } catch (error) {
    throw new Error(`Line 1 (FileHeader): ${error instanceof Error ? error.message : 'Parse error'}`);
  }
  
  const batches: Batch[] = [];
  let currentIndex = 1;
  let batchCount = 0;
  let isInsideBatch = false;
  
  // Parse all batches
  while (currentIndex < lines.length) {
    const line = lines[currentIndex];
    const lineNumber = currentIndex + 1;
    const recordType = line[0];
    
    if (recordType === '5') {
      // Batch header - check if it has critical errors first
      const line = lines[currentIndex];
      const hasLengthError = line.length !== 94;
      
      // Check SEC Code validity and CompanyIdentification
      const paddedLine = line.padEnd(94, ' ');
      const secCode = paddedLine.substring(50, 53).trim();
      const hasInvalidSecCode = !VALID_SEC_CODES.includes(secCode as SecCode);
      
      // Check if CompanyIdentification is all zeros or spaces
      const companyId = paddedLine.substring(40, 50).trim();
      const hasInvalidCompanyId = /^0+$/.test(companyId) || companyId === '';
      
      const hasCriticalError = hasLengthError || hasInvalidSecCode || hasInvalidCompanyId;
      
      if (isInsideBatch) {
        validationErrors.push({
          type: 'error',
          field: 'batchHeader',
          message: 'batch header found without closing previous batch',
          location: `Line ${lineNumber}`,
        });
      }
      
      if (hasCriticalError) {
        // If batch header has critical errors (wrong length or invalid SEC code), don't parse the batch
        // Just parse the header itself to collect errors, then skip it
        parseBatchHeader(line, lineNumber, validationErrors);
        isInsideBatch = false;
        currentIndex++;
      } else {
        // Batch header is valid, try to parse the complete batch
        try {
          const batch = parseBatch(lines, currentIndex, validationErrors);
          batches.push(batch.batch);
          currentIndex = batch.nextIndex;
          batchCount++;
          isInsideBatch = false;
          
          // Yield to browser every 10 batches for large files
          if (batchCount % 10 === 0) {
            await yieldToMain();
          }
        } catch (error) {
          // If batch parsing fails, try to continue
          validationErrors.push({
            type: 'error',
            field: 'batch',
            message: error instanceof Error ? error.message : 'Batch parse error',
            location: `Line ${lineNumber}`,
          });
          currentIndex++;
        }
      }
    } else if (recordType === '6') {
      // Entry detail outside of batch
      validationErrors.push({
        type: 'error',
        field: 'EntryDetail',
        message: 'entry outside of batch',
        location: `Line ${lineNumber}`,
      });
      currentIndex++;
    } else if (recordType === '7') {
      // Addenda outside of entry
      validationErrors.push({
        type: 'error',
        field: 'Addenda',
        message: 'addenda outside of entry',
        location: `Line ${lineNumber}`,
      });
      currentIndex++;
    } else if (recordType === '8') {
      // Batch control outside of batch
      validationErrors.push({
        type: 'error',
        field: 'BatchControl',
        message: 'batch control outside of batch',
        location: `Line ${lineNumber}`,
      });
      currentIndex++;
    } else if (recordType === '9') {
      // File control record
      break;
    } else {
      currentIndex++;
    }
  }
  
  // Parse file control (try to parse even if there are errors)
  let fileControl: FileControl;
  if (currentIndex < lines.length && lines[currentIndex] && lines[currentIndex][0] === '9') {
    try {
      fileControl = parseFileControl(lines[currentIndex], currentIndex + 1, validationErrors);
    } catch (error) {
      // If parsing fails completely, create a minimal file control
      validationErrors.push({
        type: 'error',
        field: 'FileControl',
        message: `failed to parse: ${error instanceof Error ? error.message : 'unknown error'}`,
        location: `Line ${currentIndex + 1}`,
      });
      fileControl = {
        recordTypeCode: '9',
        batchCount: '0',
        blockCount: '0',
        entryAddendaCount: '0',
        entryHash: '0',
        totalDebitAmount: '0',
        totalCreditAmount: '0',
        reserved: '',
        raw: lines[currentIndex] || '',
      };
    }
  } else {
    // File control not found
    validationErrors.push({
      type: 'error',
      field: 'FileControl',
      message: 'file control record (type 9) not found',
      location: `Line ${currentIndex + 1}`,
    });
    fileControl = {
      recordTypeCode: '9',
      batchCount: '0',
      blockCount: '0',
      entryAddendaCount: '0',
      entryHash: '0',
      totalDebitAmount: '0',
      totalCreditAmount: '0',
      reserved: '',
      raw: '',
    };
  }
  
  const file: NachaFile = {
    fileHeader,
    batches,
    fileControl,
    raw,
    lineCount: lines.length,
    validationErrors, // Store parsing errors for later
  };
  
  return file;
}

// Synchronous version kept for backward compatibility but not used
// function parseFile(lines: string[], raw: string): NachaFile {
//   if (lines.length < 4) {
//     throw new Error('File too short - minimum 4 lines required (file header, batch header, batch control, file control)');
//   }
//   
//   const fileHeader = parseFileHeader(lines[0]);
//   const batches: Batch[] = [];
//   let currentIndex = 1;
//   
//   // Parse all batches
//   while (currentIndex < lines.length) {
//     const line = lines[currentIndex];
//     const recordType = line[0];
//     
//     if (recordType === '5') {
//       // Batch header
//       const batch = parseBatch(lines, currentIndex);
//       batches.push(batch.batch);
//       currentIndex = batch.nextIndex;
//     } else if (recordType === '9') {
//       // File control record
//       break;
//     } else {
//       currentIndex++;
//     }
//   }
//   
//   const fileControl = parseFileControl(lines[currentIndex]);
//   
//   return {
//     fileHeader,
//     batches,
//     fileControl,
//     raw,
//     lineCount: lines.length,
//   };
// }

function parseFileHeader(line: string, lineNumber: number, errors: ValidationError[]): FileHeader {
  if (line.length !== 94) {
    errors.push({
      type: 'error',
      field: 'FileHeader',
      message: `must be 94 characters and found ${line.length}`,
      location: `Line ${lineNumber}`,
    });
  }
  
  // Pad line if too short to avoid substring errors
  const paddedLine = line.padEnd(94, ' ');
  
  // Validate ImmediateOrigin
  const immediateOrigin = paddedLine.substring(13, 23).trim();
  if (immediateOrigin === '000000000' || immediateOrigin === '0' || immediateOrigin === '') {
    errors.push({
      type: 'error',
      field: 'ImmediateOrigin',
      message: `${immediateOrigin || '000000000'} is a mandatory field and has a default value`,
      location: `Line ${lineNumber}`,
    });
  }
  
  return {
    recordTypeCode: '1',
    priorityCode: paddedLine.substring(1, 3).trim(),
    immediateDestination: paddedLine.substring(3, 13).trim(),
    immediateOrigin: immediateOrigin,
    fileCreationDate: paddedLine.substring(23, 29).trim(),
    fileCreationTime: paddedLine.substring(29, 33).trim(),
    fileIdModifier: paddedLine.substring(33, 34).trim(),
    recordSize: paddedLine.substring(34, 37).trim(),
    blockingFactor: paddedLine.substring(37, 39).trim(),
    formatCode: paddedLine.substring(39, 40).trim(),
    immediateDestinationName: paddedLine.substring(40, 63).trim(),
    immediateOriginName: paddedLine.substring(63, 86).trim(),
    referenceCode: paddedLine.substring(86, 94).trim(),
    raw: line,
  };
}

function parseBatch(lines: string[], startIndex: number, errors: ValidationError[]): { batch: Batch; nextIndex: number } {
  const lineNumber = startIndex + 1;
  const header = parseBatchHeader(lines[startIndex], lineNumber, errors);
  const entries: EntryDetail[] = [];
  let currentIndex = startIndex + 1;
  
  while (currentIndex < lines.length) {
    const line = lines[currentIndex];
    const recordType = line[0];
    
    if (recordType === '6') {
      // Entry detail record
      const entry = parseEntryDetail(lines, currentIndex, errors);
      entries.push(entry.entry);
      currentIndex = entry.nextIndex;
    } else if (recordType === '8') {
      // Batch control record
      const controlLineNumber = currentIndex + 1;
      const control = parseBatchControl(lines[currentIndex], controlLineNumber, errors);
      return {
        batch: { header, entries, control, controlLineNumber },
        nextIndex: currentIndex + 1,
      };
    } else {
      currentIndex++;
    }
  }
  
  throw new Error('Batch control record not found');
}

function parseBatchHeader(line: string, lineNumber: number, errors: ValidationError[]): BatchHeader {
  if (line.length !== 94) {
    errors.push({
      type: 'error',
      field: 'BatchHeader',
      message: `must be 94 characters and found ${line.length}`,
      location: `Line ${lineNumber}`,
    });
  }
  
  // Pad line if too short to avoid substring errors
  const paddedLine = line.padEnd(94, ' ');
  
  const secCode = paddedLine.substring(50, 53).trim() as SecCode;
  
  // Validate SEC Code
  if (!VALID_SEC_CODES.includes(secCode)) {
    errors.push({
      type: 'error',
      field: 'StandardEntryClassCode',
      message: `${secCode} is an invalid Standard Entry Class Code`,
      location: `Line ${lineNumber}`,
    });
  }
  
  // Validate CompanyIdentification
  const companyId = paddedLine.substring(40, 50).trim();
  const isAllZeros = /^0+$/.test(companyId);
  const isAllSpaces = companyId === '';
  if (isAllZeros || isAllSpaces) {
    errors.push({
      type: 'error',
      field: 'CompanyIdentification',
      message: `${companyId || '000000000'} contains only spaces and zeros`,
      location: `Line ${lineNumber}`,
    });
  }
  
  return {
    recordTypeCode: '5',
    serviceClassCode: paddedLine.substring(1, 4).trim(),
    companyName: paddedLine.substring(4, 20).trim(),
    companyDiscretionaryData: paddedLine.substring(20, 40).trim(),
    companyId: companyId,
    standardEntryClassCode: secCode,
    companyEntryDescription: paddedLine.substring(53, 63).trim(),
    companyDescriptiveDate: paddedLine.substring(63, 69).trim(),
    effectiveEntryDate: paddedLine.substring(69, 75).trim(),
    settlementDate: paddedLine.substring(75, 78).trim(),
    originatorStatusCode: paddedLine.substring(78, 79).trim(),
    originatingDFI: paddedLine.substring(79, 87).trim(),
    batchNumber: paddedLine.substring(87, 94).trim(),
    raw: line,
  };
}

function parseEntryDetail(lines: string[], startIndex: number, errors: ValidationError[]): { entry: EntryDetail; nextIndex: number } {
  const line = lines[startIndex];
  const lineNumber = startIndex + 1;
  
  if (line.length !== 94) {
    errors.push({
      type: 'error',
      field: 'EntryDetail',
      message: `must be 94 characters and found ${line.length}`,
      location: `Line ${lineNumber}`,
    });
  }
  
  // Pad line if too short to avoid substring errors
  const paddedLine = line.padEnd(94, ' ');
  
  const entry: EntryDetail = {
    recordTypeCode: '6',
    transactionCode: paddedLine.substring(1, 3).trim(),
    receivingDFI: paddedLine.substring(3, 11).trim(),
    checkDigit: paddedLine.substring(11, 12).trim(),
    dfaAccountNumber: paddedLine.substring(12, 29).trim(),
    amount: paddedLine.substring(29, 39).trim(),
    individualIdNumber: paddedLine.substring(39, 54).trim(),
    individualName: paddedLine.substring(54, 76).trim(),
    discretionaryData: paddedLine.substring(76, 78).trim(),
    addendaRecordIndicator: paddedLine.substring(78, 79).trim(),
    traceNumber: paddedLine.substring(79, 94).trim(),
    raw: line,
  };
  
  let currentIndex = startIndex + 1;
  
  // Check if addenda records exist
  if (entry.addendaRecordIndicator === '1') {
    entry.addenda = [];
    while (currentIndex < lines.length && lines[currentIndex][0] === '7') {
      entry.addenda.push(parseAddendaRecord(lines[currentIndex], currentIndex + 1, errors));
      currentIndex++;
    }
    
    // Validate addenda count (PPD, CCD, WEB allow only 0 or 1 addenda)
    if (entry.addenda.length > 1) {
      errors.push({
        type: 'error',
        field: 'Addenda',
        message: `entry has ${entry.addenda.length} addenda records, but only 0 or 1 addenda is allowed`,
        location: `Line ${lineNumber}`,
      });
    }
  } else if (entry.addendaRecordIndicator === '0') {
    // Addenda indicator is 0 but there might be addenda records following (error case)
    if (currentIndex < lines.length && lines[currentIndex][0] === '7') {
      errors.push({
        type: 'error',
        field: 'AddendaRecordIndicator',
        message: `addenda record indicator is 0 but addenda records found`,
        location: `Line ${lineNumber}`,
      });
    }
  }
  
  return { entry, nextIndex: currentIndex };
}

function parseAddendaRecord(line: string, lineNumber: number, errors: ValidationError[]): AddendaRecord {
  if (line.length !== 94) {
    errors.push({
      type: 'error',
      field: 'Addenda',
      message: `must be 94 characters and found ${line.length}`,
      location: `Line ${lineNumber}`,
    });
  }
  
  // Pad line if too short to avoid substring errors
  const paddedLine = line.padEnd(94, ' ');
  
  return {
    recordTypeCode: '7',
    addendaTypeCode: paddedLine.substring(1, 3).trim(),
    paymentRelatedInformation: paddedLine.substring(3, 83).trim(),
    addendaSequenceNumber: paddedLine.substring(83, 87).trim(),
    entryDetailSequenceNumber: paddedLine.substring(87, 94).trim(),
    raw: line,
  };
}

function parseBatchControl(line: string, lineNumber: number, errors: ValidationError[]): BatchControl {
  if (line.length !== 94) {
    errors.push({
      type: 'error',
      field: 'BatchControl',
      message: `must be 94 characters and found ${line.length}`,
      location: `Line ${lineNumber}`,
    });
  }
  
  // Pad line if too short to avoid substring errors
  const paddedLine = line.padEnd(94, ' ');
  
  return {
    recordTypeCode: '8',
    serviceClassCode: paddedLine.substring(1, 4).trim(),
    entryAddendaCount: paddedLine.substring(4, 10).trim(),
    entryHash: paddedLine.substring(10, 20).trim(),
    totalDebitAmount: paddedLine.substring(20, 32).trim(),
    totalCreditAmount: paddedLine.substring(32, 44).trim(),
    companyId: paddedLine.substring(44, 54).trim(),
    messageAuthenticationCode: paddedLine.substring(54, 73).trim(),
    reserved: paddedLine.substring(73, 79).trim(),
    originatingDFI: paddedLine.substring(79, 87).trim(),
    batchNumber: paddedLine.substring(87, 94).trim(),
    raw: line,
  };
}

function parseFileControl(line: string, lineNumber: number, errors: ValidationError[]): FileControl {
  if (line.length !== 94) {
    errors.push({
      type: 'error',
      field: 'FileControl',
      message: `must be 94 characters and found ${line.length}`,
      location: `Line ${lineNumber}`,
    });
  }
  
  // Pad line if too short to avoid substring errors
  const paddedLine = line.padEnd(94, ' ');
  
  const entryAddendaCount = paddedLine.substring(13, 21).trim();
  
  // Validate EntryAddendaCount is not empty/zero (mandatory field)
  if (entryAddendaCount === '' || entryAddendaCount === '00000000') {
    errors.push({
      type: 'error',
      field: 'EntryAddendaCount',
      message: `${entryAddendaCount || '00000000'} is a mandatory field and has a default value`,
      location: `Line ${lineNumber}`,
    });
  }
  
  return {
    recordTypeCode: '9',
    batchCount: paddedLine.substring(1, 7).trim(),
    blockCount: paddedLine.substring(7, 13).trim(),
    entryAddendaCount,
    entryHash: paddedLine.substring(21, 31).trim(),
    totalDebitAmount: paddedLine.substring(31, 43).trim(),
    totalCreditAmount: paddedLine.substring(43, 55).trim(),
    reserved: paddedLine.substring(55, 94).trim(),
    raw: line,
  };
}

function validateFile(file: NachaFile): ValidationResult {
  // Start with parsing errors if any
  const errors: ValidationError[] = file.validationErrors ? [...file.validationErrors] : [];
  const warnings: ValidationError[] = [];
  
  // Check if FileControl has critical errors or structural errors
  const hasFileControlErrors = errors.some(e => 
    e.field === 'EntryAddendaCount' || 
    e.field === 'FileControl' ||
    e.message.includes('mandatory field')
  );
  
  // Check for structural errors that indicate file structure is broken
  const hasStructuralErrors = errors.some(e =>
    e.message.includes('outside of batch') ||
    e.message.includes('outside of entry') ||
    e.field === 'BatchHeader' ||
    e.field === 'batchHeader' ||
    e.field === 'CompanyIdentification' ||
    e.field === 'ImmediateOrigin'
  );
  
  // Initialize totals for batch-level validation
  let totalEntries = 0;
  let totalDebit = 0;
  let totalCredit = 0;
  let totalHash = 0;
  let hasBatchEntryCountError = false; // Track if any batch has entry count errors
  let hasBatchCriticalError = false; // Track if any batch has ServiceClassCode/CompanyId errors
  
  file.batches.forEach((batch, batchIndex) => {
    const batchNum = batchIndex + 1;
    
    // Count entries
    let batchEntryCount = batch.entries.length;
    batch.entries.forEach(entry => {
      if (entry.addenda) {
        batchEntryCount += entry.addenda.length;
      }
    });
    
    // Use batch control line number if available
    const batchLocation = batch.controlLineNumber ? `Line ${batch.controlLineNumber}` : `Batch ${batchNum}`;
    
    // Validate ServiceClassCode match between header and control (critical error)
    if (batch.header.serviceClassCode !== batch.control.serviceClassCode) {
      errors.push({
        type: 'error',
        field: 'Batches',
        message: `batch #${batchNum} (${batch.header.standardEntryClassCode}) ServiceClassCode header ${batch.header.serviceClassCode} is not equal to control ${batch.control.serviceClassCode}`,
        location: batchLocation,
      });
      hasBatchCriticalError = true;
      // Skip other validations and don't accumulate totals for this batch (Moov behavior)
      return;
    }
    
    // Validate CompanyIdentification match between header and control (critical error)
    if (batch.header.companyId !== batch.control.companyId) {
      errors.push({
        type: 'error',
        field: 'Batches',
        message: `batch #${batchNum} (${batch.header.standardEntryClassCode}) CompanyIdentification header ${batch.header.companyId} is not equal to control ${batch.control.companyId}`,
        location: batchLocation,
      });
      hasBatchCriticalError = true;
      // Skip other validations and don't accumulate totals for this batch (Moov behavior)
      return;
    }
    
    // If we reach here, batch is valid - calculate and accumulate totals
    totalEntries += batchEntryCount;
    
    let batchDebit = 0;
    let batchCredit = 0;
    let batchHash = 0;
    
    batch.entries.forEach((entry, entryIndex) => {
      const amount = parseInt(entry.amount || '0');
      const transactionCode = parseInt(entry.transactionCode || '0');
      const routingNumber = parseInt(entry.receivingDFI || '0');
      
      batchHash += routingNumber;
      
      // Determine debit or credit based on transaction code
      // Debits: 21, 26, 27, 28, 29, 36, 37, 38, 39
      // Credits: 22, 23, 24, 31, 32, 33, 34
      // 21/31 = Return/Dishonored Return, 26/36 = Return for debit
      if ([21, 26, 27, 28, 29, 36, 37, 38, 39].includes(transactionCode)) {
        batchDebit += amount;
      } else if ([22, 23, 24, 31, 32, 33, 34].includes(transactionCode)) {
        batchCredit += amount;
      }
      
      // Validate account number
      if (entry.dfaAccountNumber.length === 0) {
        warnings.push({
          type: 'warning',
          field: 'DFIAccountNumber',
          message: `account number is empty`,
          location: `Batch ${batchNum} Entry ${entryIndex + 1}`,
        });
      }
    });
    
    totalDebit += batchDebit;
    totalCredit += batchCredit;
    totalHash += batchHash;
    
    // Validate batch entry count
    const expectedBatchEntryCount = parseInt(batch.control.entryAddendaCount || '0');
    if (batchEntryCount !== expectedBatchEntryCount) {
      errors.push({
        type: 'error',
        field: 'Batches',
        message: `batch #${batchNum} (${batch.header.standardEntryClassCode}) EntryAddendaCount calculated ${batchEntryCount} is out-of-balance with batch control ${expectedBatchEntryCount}`,
        location: batchLocation,
      });
      hasBatchEntryCountError = true; // Mark that we have batch-level entry count error
    }
    
    // Validate batch amounts - report first error only (Moov behavior)
    const expectedBatchDebit = parseInt(batch.control.totalDebitAmount || '0');
    const expectedBatchCredit = parseInt(batch.control.totalCreditAmount || '0');
    
    let hasBatchAmountError = false;
    
    if (batchDebit !== expectedBatchDebit) {
      errors.push({
        type: 'error',
        field: 'Batches',
        message: `batch #${batchNum} (${batch.header.standardEntryClassCode}) TotalDebitEntryDollarAmount calculated ${batchDebit} is out-of-balance with batch control ${expectedBatchDebit}`,
        location: batchLocation,
      });
      hasBatchAmountError = true;
    }
    
    // Only check credit if debit is correct (Moov stops at first amount error)
    if (!hasBatchAmountError && batchCredit !== expectedBatchCredit) {
      errors.push({
        type: 'error',
        field: 'Batches',
        message: `batch #${batchNum} (${batch.header.standardEntryClassCode}) TotalCreditEntryDollarAmount calculated ${batchCredit} is out-of-balance with batch control ${expectedBatchCredit}`,
        location: batchLocation,
      });
    }
    
    // Validate batch hash (last 10 digits)
    const batchHashStr = (batchHash % 10000000000).toString().padStart(10, '0');
    if (batchHashStr !== batch.control.entryHash) {
      errors.push({
        type: 'error',
        field: 'Batches',
        message: `batch #${batchNum} (${batch.header.standardEntryClassCode}) EntryHash calculated ${batchHashStr} is out-of-balance with batch control ${batch.control.entryHash}`,
        location: batchLocation,
      });
    }
  });
  
  // Only perform file-level validation if FileControl fields are valid, no structural errors, and no batch critical errors
  if (!hasFileControlErrors && !hasStructuralErrors && !hasBatchCriticalError) {
    // Validate batch count
    const expectedBatchCount = parseInt(file.fileControl.batchCount || '0');
    if (file.batches.length !== expectedBatchCount) {
      errors.push({
        type: 'error',
        field: 'BatchCount',
        message: `batch count calculated ${file.batches.length} is out-of-balance with file control ${expectedBatchCount}`,
        location: 'FileControl',
      });
    }
    
    // Validate file-level statistics
    // Skip EntryAddendaCount validation if batch-level errors exist (to avoid duplicate errors)
    const expectedTotalEntries = parseInt(file.fileControl.entryAddendaCount || '0');
    if (!hasBatchEntryCountError && totalEntries !== expectedTotalEntries) {
      errors.push({
        type: 'error',
        field: 'FileControl',
        message: `EntryAddendaCount calculated ${totalEntries} is out-of-balance with file control ${expectedTotalEntries}`,
        location: 'FileControl',
      });
    }
    
    const expectedTotalDebit = parseInt(file.fileControl.totalDebitAmount || '0');
    const expectedTotalCredit = parseInt(file.fileControl.totalCreditAmount || '0');
    
    if (totalDebit !== expectedTotalDebit) {
      errors.push({
        type: 'error',
        field: 'FileControl',
        message: `TotalDebitEntryDollarAmount calculated ${totalDebit} is out-of-balance with file control ${expectedTotalDebit}`,
        location: 'FileControl',
      });
    }
    
    if (totalCredit !== expectedTotalCredit) {
      errors.push({
        type: 'error',
        field: 'FileControl',
        message: `TotalCreditEntryDollarAmount calculated ${totalCredit} is out-of-balance with file control ${expectedTotalCredit}`,
        location: 'FileControl',
      });
    }
    
    // Validate file hash
    const fileHashStr = (totalHash % 10000000000).toString().padStart(10, '0');
    if (fileHashStr !== file.fileControl.entryHash) {
      errors.push({
        type: 'error',
        field: 'FileControl',
        message: `EntryHash calculated ${fileHashStr} is out-of-balance with file control ${file.fileControl.entryHash}`,
        location: 'FileControl',
      });
    }
  } else {
    if (hasBatchCriticalError) {
      console.log('⚠️ Batch critical errors detected (ServiceClassCode or CompanyId mismatch), skipping file-level statistical validation');
    } else if (hasStructuralErrors) {
      console.log('⚠️ Structural errors detected (records outside of batch), skipping file-level statistical validation');
    } else {
      console.log('⚠️ FileControl has critical errors, skipping file-level statistical validation');
    }
  }
  
  // Sort errors by line number for consistent output (Moov behavior)
  const sortedErrors = errors.sort((a, b) => {
    // Extract line number from location string (e.g., "Line 5" -> 5)
    const getLineNumber = (location?: string): number => {
      if (!location) return 999999; // Put errors without line numbers at the end
      const match = location.match(/Line (\d+)/);
      return match ? parseInt(match[1]) : 999999;
    };
    
    return getLineNumber(a.location) - getLineNumber(b.location);
  });
  
  return {
    valid: sortedErrors.length === 0,
    errors: sortedErrors,
    warnings,
  };
}

